/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import { ROUTE_PATHS } from 'routes/constants';
import { clearLocalStorage, consumeSessionExpiredFlag } from 'services/authUtils';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import './Login.scss';

const Login = ({ authStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (consumeSessionExpiredFlag()) {
      setSessionExpired(true);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (authStore.isLoginSuccess && location.pathname === ROUTE_PATHS.LOGIN) {
        authStore.resetStore();
        clearLocalStorage();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const getErrorMessage = () => {
    if (sessionExpired) return i18n.getMessage('login.error.sessionExpired');

    if (!authStore.error) return null;

    // Handle specific error messages
    if (authStore.error.message) {
      if (
        authStore.error.message.includes('Wrong username or password') ||
        authStore.error.message.includes('invalid_grant')
      ) {
        return i18n.getMessage('login.error.wrongCredentials');
      }
      if (authStore.error.message.includes('Unauthorized') || authStore.error.code === 'invalid_token') {
        return i18n.getMessage('login.error.unauthorized');
      }
      return authStore.error.message;
    }

    return i18n.getMessage('login.error.general');
  };

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      username: '',
      password: ''
    },
    initialStatus: { password: true, username: true },
    validationSchema: Yup.object({
      username: Yup.string().nullable(true).required(i18n.getMessage('validation.emptyField')),
      password: Yup.string()
        .nullable(true)
        .min(7, i18n.getMessage('validation.passwordRequirements'))
        .max(30, i18n.getMessage('validation.fieldSize', { amount: '30' }))
        .required(i18n.getMessage('login.error.emptyFiled', { field: 'password' }))
    }),
    onSubmit: async (values, { setSubmitting }) => {
      // Clear any previous errors
      authStore.error = null;

      try {
        await authStore.sendAuthorizationData(values);

        // Only navigate if login was successful (no error in authStore)
        if (!authStore.error) {
          await authStore.getUserRoles(true);
          const redirectPath = searchParams.get('redirectPath') || ROUTE_PATHS.CUSTOMERS;
          navigate(decodeURIComponent(redirectPath));
        }
      } catch (error) {
        // Error is already handled in authStore
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { values, errors, handleSubmit, handleChange: formHandleChange, submitCount } = form;

  // Custom handleChange to clear errors when user types
  const handleChange = (e) => {
    if (authStore.error) {
      authStore.error = null;
    }
    if (sessionExpired) {
      setSessionExpired(false);
    }
    formHandleChange(e);
  };

  return (
    <section className={'authorization-form login'}>
      <h1 className={'authorization-title'}>{i18n.getMessage('login.title')}</h1>
      <Alert type={'warning'} message={getErrorMessage()} />
      <form
        id={'send'}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
        className={'form'}
      >
        <div className={'form-inputs-wrapper'}>
          <Input
            type={'text'}
            label={i18n.getMessage('login.input.login')}
            name={'username'}
            autoComplete={'off'}
            value={values.username}
            onChange={handleChange}
            initialStatus={submitCount}
            error={errors?.username}
            isApiError={!!authStore.error}
          />
          <Input
            type={'password'}
            name={'password'}
            label={i18n.getMessage('login.input.password')}
            placeholder={i18n.getMessage('login.placeholder.password')}
            autoComplete={'off'}
            value={values.password}
            onChange={handleChange}
            initialStatus={submitCount}
            error={errors?.password}
            isApiError={!!authStore.error}
          />
        </div>
        <Button
          type={'primary'}
          roleType={'submit'}
          size={'large'}
          fullWidth={true}
          onClick={() => {}}
          isDisabled={authStore.isLoading}
        >
          {authStore.isLoading ? <Loader /> : i18n.getMessage('login.button')}
        </Button>
      </form>
    </section>
  );
};

Login.propTypes = {
  authStore: MobXPropTypes.observableObject
};

export default inject('authStore')(observer(Login));
